import { useState, useRef, useEffect, useMemo } from 'react'
import { useDebounce } from '@/hooks/useDebounce'
import { useSearchClubMembers } from '@/services'
import { useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { OFFSET } from '@/constants/Common'
import { ClubMember } from '@/types'

export interface MatchFilterState {
  dateFrom: string
  dateTo: string
  playerName: string
  playerId: number | null
  matchTypes: string[]
}

export const EMPTY_MATCH_FILTER: MatchFilterState = {
  dateFrom: '',
  dateTo: '',
  playerName: '',
  playerId: null,
  matchTypes: [],
}

const MATCH_TYPES = [
  { value: 'SIDE_ONLY', label: 'Side Only' },
  { value: 'RALLY', label: 'Rally' },
]

export const countActiveMatchFilters = (f: MatchFilterState): number => {
  let n = 0
  if (f.dateFrom || f.dateTo) n++
  if (f.playerId) n++
  if (f.matchTypes.length > 0) n++
  return n
}

export const describeActiveMatchFilters = (f: MatchFilterState): string[] => {
  const chips: string[] = []

  if (f.dateFrom && f.dateTo) chips.push(`Date ${f.dateFrom} to ${f.dateTo}`)
  else if (f.dateFrom) chips.push(`After ${f.dateFrom}`)
  else if (f.dateTo) chips.push(`Before ${f.dateTo}`)

  if (f.playerId && f.playerName) chips.push(`Player: "${f.playerName}"`)

  if (f.matchTypes.length > 0) {
    chips.push(`Type: ${f.matchTypes.map((t) => MATCH_TYPES.find((mt) => mt.value === t)?.label ?? t).join(', ')}`)
  }

  return chips
}

const SectionHeader = ({ label }: { label: string }) => (
  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{label}</div>
)

const SmallInput = ({ value, onChange, placeholder, type = 'text' }: {
  value: string; onChange: (v: string) => void; placeholder: string; type?: string
}) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full px-1.5 py-1 border border-gray-200 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-400"
  />
)

interface MatchFilterPopoverProps {
  filter: MatchFilterState
  onChange: (filter: MatchFilterState) => void
  clubId: number
}

export const MatchFilterPopover = ({ filter, onChange, clubId }: MatchFilterPopoverProps) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { user } = useAppSelector(userSelector)

  // Player search state (local to popover)
  const [playerSearch, setPlayerSearch] = useState(filter.playerName)
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false)
  const debouncedPlayerSearch = useDebounce<string>(playerSearch || '', 300)

  const { data: playerResults } = useSearchClubMembers({
    clubId,
    exclude: [],
    filter: {
      lat: user?.addresses?.[0]?.latitude || null,
      lng: user?.addresses?.[0]?.longitude || null,
    },
    includePendingPlayers: true,
    includeStaff: true,
    includeUnclaimedPlayers: true,
    limit: 10,
    offset: OFFSET,
    query: debouncedPlayerSearch.length >= 2 ? debouncedPlayerSearch : '*',
  })

  const activeCount = useMemo(() => countActiveMatchFilters(filter), [filter])

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Sync local player search with filter when popover opens
  useEffect(() => {
    if (open) setPlayerSearch(filter.playerName)
  }, [open, filter.playerName])

  const toggleMatchType = (value: string) => {
    const arr = filter.matchTypes
    const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
    onChange({ ...filter, matchTypes: next })
  }

  const onSelectPlayer = (member: ClubMember) => {
    onChange({ ...filter, playerName: member.fullName, playerId: member.id })
    setPlayerSearch(member.fullName)
    setShowPlayerDropdown(false)
  }

  const onClearPlayer = () => {
    onChange({ ...filter, playerName: '', playerId: null })
    setPlayerSearch('')
    setShowPlayerDropdown(false)
  }

  const onPlayerSearchChange = (v: string) => {
    setPlayerSearch(v)
    setShowPlayerDropdown(v.length >= 2)
    // Clear the selected player when user edits
    if (filter.playerId) {
      onChange({ ...filter, playerName: '', playerId: null })
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 px-2 py-1.5 border text-xs transition-colors ${
          activeCount > 0
            ? 'border-primary-300 bg-primary-50 text-primary-700'
            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
        }`}
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        Filters
        {activeCount > 0 && (
          <span className="bg-primary-600 text-white text-[9px] font-bold px-1 py-0.5 leading-none">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 shadow-lg w-64 max-h-[70vh] overflow-y-auto">
          {/* Date range */}
          <div className="p-2 border-b border-gray-100">
            <SectionHeader label="Match Date" />
            <div className="flex items-center gap-1.5">
              <SmallInput value={filter.dateFrom} onChange={(v) => onChange({ ...filter, dateFrom: v })} placeholder="From" type="date" />
              <span className="text-gray-300 text-[10px]">-</span>
              <SmallInput value={filter.dateTo} onChange={(v) => onChange({ ...filter, dateTo: v })} placeholder="To" type="date" />
            </div>
          </div>

          {/* Player autocomplete */}
          <div className="p-2 border-b border-gray-100">
            <SectionHeader label="Player" />
            <div className="relative">
              <input
                type="text"
                value={playerSearch}
                onChange={(e) => onPlayerSearchChange(e.target.value)}
                placeholder="Search player name..."
                className="w-full px-1.5 py-1 border border-gray-200 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
              {filter.playerId && (
                <button
                  onClick={onClearPlayer}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {showPlayerDropdown && playerResults?.result?.hits && playerResults.result.hits.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-0.5 bg-white border border-gray-200 shadow-lg max-h-40 overflow-y-auto z-50">
                  {playerResults.result.hits.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => onSelectPlayer(member)}
                      className="flex items-center gap-1.5 w-full px-2 py-1.5 text-left hover:bg-gray-50 border-b border-gray-50 last:border-0"
                    >
                      {member.imageUrl ? (
                        <img src={member.imageUrl} className="w-5 h-5 rounded-full flex-shrink-0" alt="" />
                      ) : (
                        <div className="w-5 h-5 rounded-full bg-gray-200 flex-shrink-0" />
                      )}
                      <span className="text-xs text-gray-900 truncate">{member.fullName}</span>
                      <span className="text-[10px] text-gray-400 ml-auto flex-shrink-0">{member.duprId}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Match type */}
          <div className="p-2 border-b border-gray-100">
            <SectionHeader label="Match Type" />
            <div className="flex flex-wrap gap-1">
              {MATCH_TYPES.map(({ value, label }) => {
                const active = filter.matchTypes.includes(value)
                return (
                  <button
                    key={value}
                    onClick={() => toggleMatchType(value)}
                    className={`px-2 py-0.5 text-[10px] border transition-colors ${
                      active
                        ? 'border-primary-400 bg-primary-50 text-primary-700 font-medium'
                        : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Clear all */}
          {activeCount > 0 && (
            <div className="p-1.5">
              <button
                onClick={() => {
                  onChange(EMPTY_MATCH_FILTER)
                  setPlayerSearch('')
                  setShowPlayerDropdown(false)
                }}
                className="w-full text-center text-[11px] text-gray-500 hover:text-gray-700 py-0.5"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
